import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useCallback, useState } from 'react'
import { useUser, useUpdateUser } from '../../../hooks/admin/useUsers'
import { useAuthContext } from '../../../contexts/AuthContext'
import { UserEditTabs, type UserEditTab } from '../../../components/admin/users/UserEditTabs'
import { GroupsTab } from '../../../components/admin/users/GroupsTab'
import { SecurityTab } from '../../../components/admin/users/SecurityTab'
import { DangerTab } from '../../../components/admin/users/DangerTab'
import type { UserUpdateRequest } from '../../../types/admin'

const ROLE_OPTIONS: Array<{ value: 'admin' | 'partner' | 'customer' | 'guest'; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'partner', label: 'Partner' },
  { value: 'customer', label: 'Customer' },
  { value: 'guest', label: 'Guest' },
]

const TAB_IDS: UserEditTab[] = ['profile', 'groups', 'manage']

// Security and Danger were separate tabs before they merged into Manage.
// Anything already linked or bookmarked still lands somewhere sensible.
const LEGACY_TABS: Record<string, UserEditTab> = {
  security: 'manage',
  danger: 'manage',
}

const isValidTab = (tab: string | undefined): tab is UserEditTab =>
  tab !== undefined && (TAB_IDS as string[]).includes(tab)

const resolveTab = (tab: string | undefined): UserEditTab =>
  isValidTab(tab) ? tab : LEGACY_TABS[tab ?? ''] ?? 'profile'

export default function UserEdit() {
  const { id, tab } = useParams<{ id: string; tab?: string }>()
  const navigate = useNavigate()
  const userId = parseInt(id || '0')

  // Tab state lives in the URL so a particular tab can be linked to and
  // survives a refresh, matching how SystemHealthPage does it. An unknown tab
  // falls back to profile rather than rendering nothing.
  const activeTab: UserEditTab = resolveTab(tab)

  const { data, isLoading } = useUser(userId)
  const updateUser = useUpdateUser()
  const { email: currentUserEmail } = useAuthContext()

  const [groupsDirty, setGroupsDirty] = useState(false)

  const isEditingSelf = !!(
    currentUserEmail &&
    data?.user?.email &&
    currentUserEmail.toLowerCase() === data.user.email.toLowerCase()
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm<UserUpdateRequest>({
    values: data?.user
      ? {
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          mobileNumber: data.user.mobileNumber || '',
          accountId: data.user.accountId || '',
          supabaseId: data.user.supabaseId || null,
          role: data.user.role,
          enabled: data.user.enabled,
          failures: data.user.failures,
          description: data.user.description || '',
        }
      : undefined,
  })

  const currentRole = watch('role')

  const onSubmit = async (formData: UserUpdateRequest) => {
    try {
      // groupIds is deliberately absent: the API leaves group access untouched
      // when it is omitted, and group access is the Groups tab's to save. A
      // profile save must never commit a stale copy of it.
      await updateUser.mutateAsync({ id: userId, data: formData })
      reset(formData)
    } catch (error) {
      console.error('Failed to update user:', error)
    }
  }

  // Stable so GroupsTab's effect does not re-fire on every parent render.
  const handleGroupsDirtyChange = useCallback((dirty: boolean) => setGroupsDirty(dirty), [])

  const dirtyTabs: UserEditTab[] = [
    ...(isDirty ? (['profile'] as UserEditTab[]) : []),
    ...(groupsDirty ? (['groups'] as UserEditTab[]) : []),
  ]

  // Changing tab unmounts the current one, so unsaved edits would go with it.
  // Ask before that happens rather than losing them quietly.
  const handleTabChange = (next: UserEditTab) => {
    if (next === activeTab) return
    const leavingDirty =
      (activeTab === 'profile' && isDirty) || (activeTab === 'groups' && groupsDirty)
    if (leavingDirty && !window.confirm('You have unsaved changes on this tab. Leave without saving?')) {
      return
    }
    if (activeTab === 'profile' && isDirty) reset()
    navigate(`/admin/users/${userId}/edit/${next}`)
  }

  const handleCancel = () => {
    reset()
    navigate('/admin/users')
  }

  if (isLoading) return <div>Loading user...</div>
  if (!data || !data.user) return <div>User not found</div>

  const roleBadgeStyles: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800 border-purple-200',
    partner: 'bg-blue-100 text-blue-800 border-blue-200',
    customer: 'bg-green-100 text-green-800 border-green-200',
    guest: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  }
  const roleLabels: Record<string, string> = {
    admin: 'Admin',
    partner: 'Partner',
    customer: 'Customer',
    guest: 'Guest',
  }
  const displayRole = (currentRole || data.user.role || 'customer').toLowerCase()
  const roleBadgeClass = roleBadgeStyles[displayRole] || 'bg-gray-100 text-gray-800 border-gray-200'
  const roleLabel = roleLabels[displayRole] || displayRole

  const assignedGroupCount = data.groups.filter((g) => g.assigned).length

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <h1 className="text-3xl font-bold">Edit User</h1>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold uppercase tracking-wide border ${roleBadgeClass}`}
        >
          {roleLabel}
        </span>
      </div>
      <p className="text-gray-600 mb-6">
        {data.user.firstName} {data.user.lastName} &middot; {data.user.email}
      </p>

      {/* Identity metadata. Read-only, and the same on every tab, so it sits
          above them rather than inside one. The Supabase UUID lives here for
          that reason: it is what links this row to its auth account, not a
          field anyone should be retyping. */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">User ID:</span>
            <span className="ml-2 font-medium">{data.user.userId}</span>
          </div>
          <div>
            <span className="text-gray-600">Logins:</span>
            <span className="ml-2 font-medium">{data.user.logins}</span>
          </div>
          <div>
            <span className="text-gray-600">Fails:</span>
            <span className="ml-2 font-medium">{data.user.failures}</span>
          </div>
          <div className="col-span-2 md:col-span-1">
            <span className="text-gray-600">Supabase UUID:</span>
            <span
              className="ml-2 font-mono text-xs break-all"
              title={data.user.supabaseId || 'No linked auth account'}
            >
              {data.user.supabaseId || <span className="text-gray-400 font-sans">None</span>}
            </span>
          </div>
        </div>
      </div>

      <UserEditTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        groupCount={assignedGroupCount}
        dirtyTabs={dirtyTabs}
      />

      <div className="mt-6">
        {activeTab === 'profile' && (
          <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6">
            {/* Submitted unchanged so a profile save cannot drop the auth link.
                Shown read-only in the metadata strip above. */}
            <input type="hidden" {...register('supabaseId')} />

            <div className="space-y-6 max-w-3xl">
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">First Name *</label>
                  <input
                    type="text"
                    {...register('firstName', { required: 'First name is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Last Name *</label>
                  <input
                    type="text"
                    {...register('lastName', { required: 'Last name is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Mobile Number</label>
                  <input
                    type="tel"
                    {...register('mobileNumber')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Account</label>
                  <input
                    type="text"
                    {...register('accountId')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium mb-2">Role *</label>
                  <select
                    {...register('role', { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-2 pb-2">
                  <input type="checkbox" {...register('enabled')} className="rounded border-gray-300" />
                  <span className="text-sm font-medium">Enabled</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea
                  {...register('description')}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={!isDirty || updateUser.isPending}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateUser.isPending ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
                >
                  Cancel
                </button>
                {isDirty && <span className="text-sm text-amber-600">Unsaved changes</span>}
              </div>
            </div>
          </form>
        )}

        {activeTab === 'groups' && (
          <GroupsTab userId={userId} groups={data.groups} onDirtyChange={handleGroupsDirtyChange} />
        )}

        {/* Manage: the things done TO an account rather than edited on it.
            Each acts immediately, so there is no save to lose by pairing them,
            and delete sits last behind its own confirmation. */}
        {activeTab === 'manage' && (
          <div className="space-y-6">
            <SecurityTab userId={userId} />
            {!isEditingSelf && (
              <DangerTab userId={userId} userLabel={data.user.email} isEditingSelf={isEditingSelf} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
